/** Rate drafts waiting for the owner, and what "approve together" did. The server decides; this only shapes it. */

export type DraftDesign = {
  id: string;
  designId: string;
  designNumber: string;
  onActiveLoom?: boolean;
  drafts: { id: string; versionNo: number; effectiveFrom: string; effectiveTo?: string | null; lineCount?: number }[];
};

export type ApproveResult = {
  designId?: string;
  designNumber?: string;
  versionNo?: number;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  status: string;
  reason?: string | null;
};

/** Designs whose drafts the active-looms action would approve (all of them when none is ticked). */
export const activeLoomDesigns = (designs: DraftDesign[], picked: Set<string>): DraftDesign[] =>
  designs.filter((d) => d.onActiveLoom && (!picked.size || picked.has(d.designId)));

/** Not-approved rows first, then approved, each in design order. */
export const sortResults = (results: ApproveResult[]): ApproveResult[] =>
  [...results].sort(
    (a, b) =>
      Number(a.status === "APPROVED") - Number(b.status === "APPROVED") ||
      String(a.designNumber || "").localeCompare(String(b.designNumber || ""), undefined, { numeric: true }) ||
      String(a.effectiveFrom || "").localeCompare(String(b.effectiveFrom || ""))
  );

const KNOWN = new Set(["designs", "approved", "notApproved", "results", "repriced"]);

/**
 * Any other list in the server's answer (e.g. entries not repriced because their pay period is locked),
 * as plain lines: the item's reason/message first, then its other short values. Unknown shapes still show.
 */
export const otherNotes = (out: Record<string, any> | null | undefined): { key: string; lines: string[] }[] => {
  if (!out) return [];
  return Object.entries(out)
    .filter(([k, v]) => !KNOWN.has(k) && Array.isArray(v) && v.length)
    .map(([key, list]) => ({
      key,
      lines: (list as any[]).map((item) => {
        if (item === null || typeof item !== "object") return String(item);
        const lead = item.reason || item.message || item.problem || "";
        const rest = Object.entries(item)
          .filter(([k, v]) => !["reason", "message", "problem"].includes(k) && !/id$/i.test(k) && v !== null && v !== undefined && typeof v !== "object")
          .map(([, v]) => String(v));
        return [rest.join(" · "), lead].filter(Boolean).join(": ");
      }),
    }));
};

/** "notRepriced" → "Not repriced". */
export const noteTitle = (key: string) => {
  const words = key.replace(/([A-Z])/g, " $1").replace(/[_-]+/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
